import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { formatDate } from 'date-fns';
import { firstValueFrom } from 'rxjs';
import { jsPDF } from 'jspdf';
import { Attachment } from 'src/app/interfaces/api/attachment';
import { Image } from 'src/app/interfaces/api/image';
import { ImageStatus, StatusLabel } from 'src/app/interfaces/api/imageStatus';
import { LabelProject } from 'src/app/interfaces/api/labelProject';
import { Project } from 'src/app/interfaces/api/project';
import { Tag } from 'src/app/interfaces/shared/tag';
import { AttachmentService } from 'src/app/services/api/attachments/attachment.service';
import { MetricService } from 'src/app/services/api/metrics/metric.service';
import { Risk } from '../../interfaces/api/metric';
import { groupRisksByImage, iconNameForTitle, isFaceTag, isRisk } from '../../classes/risks/risk-group';
import autoTable from 'jspdf-autotable';
import { GoogleMap } from '@angular/google-maps';
import { domToCanvas } from 'modern-screenshot';

@Injectable({
    providedIn: 'root'
})
export class PDFTool {
  public attachments: Attachment[] = [];
  public images: Image[] = [];
  pdf!: jsPDF;

  constructor(private attachmentService: AttachmentService, private translate: TranslateService, private metricService: MetricService) {
    this.pdf = new jsPDF({ orientation: 'l', unit: 'px', format: 'a4', compress: true });
  }

  private async getCanvas(maps: any): Promise<HTMLCanvasElement | null> {
    try {
      return await domToCanvas(maps.nativeElement);
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  private getMarkerPos(risk: Risk, markers: { [id: string]: google.maps.Marker }, projection?: google.maps.MapCanvasProjection): { x: number, y: number } {
    const marker = markers[risk.id];
    if (!marker || !projection) return { x: -10, y: -10 };
    const position = marker.getPosition();
    if (!position) return { x: -10, y: -10 };
    const point = projection.fromLatLngToContainerPixel(position);
    return {
      x: Math.round(point!.x),
      y: Math.round(point!.y)
    };
  }

  private async getMapData(m: Risk, mapSize: number, mapContext: {
    map: GoogleMap,
    maps: any,
    projection?: google.maps.MapCanvasProjection,
    markers: { [id: string]: google.maps.Marker },
    mapWidth: number,
    mapHeight: number,
    getMarkerIcon: (risk: Risk) => any
  }): Promise<{ mapImagePng: string | null, markerPos: { x: number, y: number } | null, mapWidth: number, mapHeight: number } | null> {
    if (!m.payload.lat || !m.payload.lon) return null;

    const marker = mapContext.markers[m.id];
    if (marker) {
      marker.setMap(mapContext.map.googleMap!);
      marker.setVisible(true); // compound-image markers are hidden in normal view (Phase 2)
      marker.setIcon(mapContext.getMarkerIcon(m));
    }
    await new Promise(res => setTimeout(res, 200));
    const canvas = await this.getCanvas(mapContext.maps);
    if (marker) marker.setMap(null);

    return {
      mapImagePng: canvas ? canvas.toDataURL('image/jpeg', 0.8) : null,
      markerPos: this.getMarkerPos(m, mapContext.markers, mapContext.projection),
      mapWidth: mapContext.mapWidth,
      mapHeight: mapContext.mapHeight
    };
  }

  async exportRisksToPDF(params: {
    risks: Risk[],
    project: Project,
    startDate: Date | string,
    endDate: Date | string | null,
    labelProject?: LabelProject,
    onProgress?: (current: number, total: number) => void,
    mapContext?: {
      map: GoogleMap,
      maps: any,
      projection?: google.maps.MapCanvasProjection,
      markers: { [id: string]: google.maps.Marker },
      mapWidth: number,
      mapHeight: number,
      getMarkerIcon: (risk: Risk) => any
    }
  }): Promise<void> {
    let { risks, project, startDate, endDate, labelProject, onProgress, mapContext } = params;
    risks = risks.filter(isRisk);
    this.pdf = new jsPDF({ orientation: 'l', unit: 'px', format: 'a4', compress: true });
    this.pdf.setFont('helvetica', 'normal');
    let textLeft = 500;
    let titleY = 16;
    let mSize = 290;
    let pSize = 80;
    let mapSize = 130;
    let width = this.pdf.internal.pageSize.getWidth();
    let height = this.pdf.internal.pageSize.getHeight();
    let margin = 10;

    const dateRange = endDate
      ? `${formatDate(startDate, 'dd/MM/yy HH:mm')} - ${formatDate(endDate, 'dd/MM/yy HH:mm')}`
      : `${formatDate(startDate, 'dd/MM/yy HH:mm')}`;

    // Recap page
    this.pdf.addImage("/img/export-bg.jpg", "JPEG", 0, 0, width, height);

    if (project.picture) {
      const imgProps = this.pdf.getImageProperties(project.picture);
      const imgRatio = imgProps.width / imgProps.height;
      this.pdf.addImage(
        imgProps.data,
        imgProps.fileType,
        margin,
        height - pSize - margin,
        pSize * imgRatio,
        pSize
      );
    }

    const isFr = this.translate.currentLang === 'fr-FR';
    const generatedLabel = isFr
      ? `Généré le ${formatDate(new Date(), 'dd/MM/yyyy à HH:mm')}`
      : `Created: ${formatDate(new Date(), 'MM/dd/yyyy hh:mm a')}`;
    this.pdf.setTextColor("#999999");
    this.pdf.setFontSize(8);
    this.pdf.text(generatedLabel, margin, height - margin);

    let y = 30;

    this.pdf.setTextColor("#000000");
    this.pdf.setFontSize(18);
    this.pdf.text(this.translate.instant('risks.risk_report'), width / 2, y, { align: "center" });
    y += 18;

    this.pdf.setTextColor("#000000");
    this.pdf.setFontSize(21);
    this.pdf.text(project.name, width / 2, y, { align: "center" });
    y += 18;

    this.pdf.setTextColor("#666666");
    this.pdf.setFontSize(14);
    this.pdf.text(dateRange, width / 2, y, { align: "center" });
    y += 18;

    const riskAverages = await firstValueFrom(this.metricService.getAverageRisks({
      endDate: endDate ? new Date(endDate).toISOString() : undefined
    }));
    // Build table summary
    const riskArray: { title: string; count: number; average: string; dayCount: number }[] = [];
    for (const r of risks) {
      const displayTitle = r.payload['displayTitle'] || r.payload['title'] || "";
      const payloadTitle = r.payload['title'] || "";
      const index = riskArray.findIndex(_r => _r.title === displayTitle);
      if (index === -1) {
        const average = riskAverages.find((ra: any) => ra._id === payloadTitle);
        riskArray.push({
          title: displayTitle,
          count: 1,
          average: average ? average.avgPerDay.toFixed(2) : '',
          dayCount: average ? average.daysCounted : 0
        });
      } else {
        riskArray[index]!.count++;
      }
    }
    riskArray.sort((a, b) => b.count - a.count);
    riskArray.push({ title: 'TOTAL', count: risks.length, average: '', dayCount: 0 });

    autoTable(this.pdf, {
      margin: { top: y },
      head: [[
        this.translate.instant('risks.risk'),
        this.translate.instant('risks.count'),
        this.translate.instant('risks.average'),
        this.translate.instant('risks.day_count'),
        ''
      ]],
      body: riskArray.map(risk => [
        risk.title,
        risk.count,
        risk.average,
        risk.dayCount || '',
        {
          content: '',
          styles: {
            fillColor: (typeof risk.average === 'number' && risk.count > risk.average)
            ? '#ff0000'
            : undefined
          }
        }
      ]),
    });

    this.pdf.addPage();

    onProgress?.(0, risks.length);

    // One page per image for every caller. For the stream/label report the boxes come from
    // the imageStatus labels (matched by the shared tag id); on the risks page they come from
    // the attachment's risk tags.
    await this.renderRiskImagePages(
      risks, project, dateRange,
      { margin, mSize, mapSize, textLeft, titleY, width, height },
      onProgress, mapContext, labelProject
    );

    this.pdf.save(this.translate.instant('risks.title') + '-' + formatDate(startDate, 'yyyy-MM-dd') + '.pdf');
  }

  // One page per image (all callers): annotated photo + numbered pastilles, lightened right
  // column, compact map, and a "Risques détectés (N)" 3x2 grid. Numbers come from grouping (RG-10).
  private async renderRiskImagePages(
    risks: Risk[],
    project: Project,
    dateRange: string,
    L: { margin: number; mSize: number; mapSize: number; textLeft: number; titleY: number; width: number; height: number },
    onProgress?: (current: number, total: number) => void,
    mapContext?: any,
    labelProject?: LabelProject
  ): Promise<void> {
    const { margin, mSize, mapSize, textLeft, titleY, width, height } = L;
    const RED = '#d9534f';

    await this.loadRiskIcons();

    // Source per attachment id: an Attachment (risks page, boxes in tags.risk) or an
    // Image (stream/label report, boxes in statuses[].labels). Both expose `url`.
    const sourceById: Record<string, any> = {};
    for (const img of (this.images || [])) if (img?.attachment) sourceById[img.attachment] = img;
    for (const att of (this.attachments || [])) if (att?.id) sourceById[att.id] = att;
    for (const r of risks) { const g = (r as any).gallery; if (g?.id) sourceById[g.id] = g; }

    const images = groupRisksByImage(risks, sourceById);

    onProgress?.(0, images.length);

    for (let i = 0; i < images.length; i++) {
      const image = images[i]!;
      const isMulti = image.risks.length > 1; // numbers only make sense for compound images
      const first: any = image.risks[0]!.metric;
      const att: any = image.attachment ?? first.gallery ?? this.getImage(first.payload.attachment as string);

      this.pdf.setFont('helvetica', 'normal');
      this.pdf.addImage("/img/export-bg.jpg", "JPEG", 0, 0, width, height);
      await new Promise(res => setTimeout(res, 5));

      // Annotated photo + numbered pastilles (top-left)
      if (att?.url) {
        const imgProps = this.pdf.getImageProperties(att.url);
        const imgHeight = mSize - 20;
        const imgWidth = imgHeight * (imgProps.width / imgProps.height);
        this.pdf.addImage(imgProps.data, imgProps.fileType, margin, margin, imgWidth, imgHeight);

        for (const entry of image.risks) {
          const bb = this.resolveBoxBB(entry, att, labelProject);
          if (!bb) continue;
          const { left, top, width: bw, height: bh } = bb;
          if (![left, top, bw, bh].every(v => Number.isFinite(v))) continue;
          const x = margin + left * imgWidth;
          const y = margin + top * imgHeight;
          const w = bw * imgWidth;
          const h = bh * imgHeight;
          this.pdf.setDrawColor(RED);
          this.pdf.setLineWidth(0.5);
          this.pdf.rect(Math.floor(x), Math.floor(y), Math.floor(w), Math.floor(h));
          if (isMulti) this.drawPastille(x + w - 2, y + h - 2, entry.number, RED, 7);
        }
        this.pdf.setDrawColor('#000000');
        this.pdf.setLineWidth(0.2);
      }

      // Right column (lightened): project / resource / detection / zone / sequence (no Notes)
      let y = titleY + 10;
      this.pdf.setTextColor('#000000'); this.pdf.setFontSize(12);
      this.pdf.text(this.translate.instant('risks.risk_report'), textLeft, y, { maxWidth: 80 }); y += 12;
      this.pdf.setTextColor('#666666'); this.pdf.setFontSize(7);
      this.pdf.text(dateRange, textLeft, y, { maxWidth: 80 }); y += 12;
      this.pdf.setTextColor('#666666'); this.pdf.setFontSize(9);
      this.pdf.text(this.translate.instant('projects.project'), textLeft, y, { maxWidth: 80 }); y += 12;
      this.pdf.setTextColor('#000000'); this.pdf.setFontSize(10);
      this.pdf.text(project.name, textLeft, y, { maxWidth: 80 });
      y += this.pdf.splitTextToSize(project.name, 80).length * 12 + 6;
      if (first.unitName) {
        this.pdf.setTextColor('#666666'); this.pdf.setFontSize(9);
        this.pdf.text(this.translate.instant('units.unit'), textLeft, y, { maxWidth: 80 }); y += 12;
        this.pdf.setTextColor('#000000'); this.pdf.setFontSize(10);
        this.pdf.text(first.unitName, textLeft, y, { maxWidth: 80 });
        y += this.pdf.splitTextToSize(first.unitName, 80).length * 12 + 6;
      }
      const details = [formatDate(first.createdDate, 'yyyy-MM-dd HH:mm')];
      if (project.reportParams?.zone !== false) details.push(`\r\nZone: ${first.payload.zone || 'N/A'}`);
      if (project.reportParams?.sequence !== false) details.push(`\r\nSequence: ${first.payload.sequence || 'N/A'}`);
      this.pdf.setTextColor('#000000'); this.pdf.setFontSize(8);
      this.pdf.text(details.join('\r\n'), textLeft, y, { maxWidth: 80 });

      // Compact map (bottom-left) when available. Compound images show the number icon
      // on the map; single-risk images keep their risk icon.
      let mapAreaW = 0;
      if (mapContext) {
        let ctx = mapContext;
        if (image.risks.length > 1) {
          const numPng = await this.numberIconPng(image.risks.length, RED);
          if (numPng) ctx = { ...mapContext, getMarkerIcon: () => numPng };
        }
        const mapData = await this.getMapData(first, mapSize, ctx);
        if (mapData?.mapImagePng && mapData.mapWidth) {
          const mw = mapSize * mapData.mapWidth / mapData.mapHeight;
          this.pdf.addImage(mapData.mapImagePng, "JPEG", margin, mSize, mw, mapSize, undefined, 'FAST');
          mapAreaW = mw + 12;
        }
      }

      // "Risques détectés (N)" grid — 3 columns x 2 rows max (6), fixed cells, clipped overflow
      const gridX = margin + mapAreaW;
      const gridTop = mSize;
      this.pdf.setTextColor('#222222'); this.pdf.setFontSize(11);
      const gridTitle = isMulti
        ? `${this.translate.instant('risks.detected_risks')} (${image.risks.length})`
        : this.translate.instant('risks.detected_risk');
      this.pdf.text(gridTitle, gridX, gridTop + 6);

      const cols = 3, rows = 2;
      const cellW = (width - gridX - margin) / cols;
      const cellsTop = gridTop + 16;
      const cellH = (height - cellsTop - margin) / rows;
      const count = Math.min(image.risks.length, cols * rows);

      const ICON = 16;
      for (let c = 0; c < count; c++) {
        const entry = image.risks[c]!;
        const cx = gridX + (c % cols) * cellW;
        const cy = cellsTop + Math.floor(c / cols) * cellH;

        const iconPng = await this.riskIconPng(entry.metric.payload.title, RED);
        if (iconPng) this.pdf.addImage(iconPng, 'PNG', cx, cy, ICON, ICON);
        // number badge at the pictogram's top-right (white bg, red border, red font) — compound only
        if (isMulti) this.drawNumberBadge(cx + ICON - 2, cy + 2, entry.number, 5, RED);

        const name = entry.metric.payload['displayTitle'] || entry.metric.payload['title'] || '';
        const nameLines = this.pdf.splitTextToSize(name, cellW - ICON - 12).slice(0, 2);
        this.pdf.setTextColor('#222222'); this.pdf.setFontSize(10);
        this.pdf.text(nameLines, cx + ICON + 8, cy + (nameLines.length > 1 ? 6 : ICON / 2 + 3));

        let ty = cy + ICON + 10;
        const maxTy = cy + cellH - 4;

        // Suggested actions: already a comma-joined string — render as-is (no bullets), clamped with "…"
        const desc = String(entry.metric.payload.description || '').trim();
        if (desc) {
          this.pdf.setTextColor('#666666'); this.pdf.setFontSize(8);
          ty = this.drawClampedText(desc, cx, ty, cellW - 8, maxTy, 9);
        }

        const comment = String(entry.metric.payload.comment || '').trim();
        if (comment && ty <= maxTy) {
          ty += 3;
          const startY = ty;
          this.pdf.setFont('helvetica', 'italic');
          this.pdf.setTextColor('#666666'); this.pdf.setFontSize(8);
          ty = this.drawClampedText(comment, cx + 6, ty, cellW - 14, maxTy, 9);
          this.pdf.setFont('helvetica', 'normal');
          // vertical citation bar to the left of the comment
          this.pdf.setDrawColor('#bbbbbb'); this.pdf.setLineWidth(1);
          this.pdf.line(cx + 1, startY - 6, cx + 1, ty - 7);
          this.pdf.setLineWidth(0.2); this.pdf.setDrawColor('#000000');
        }
      }

      onProgress?.(i + 1, images.length);
      if (i < images.length - 1) this.pdf.addPage();
    }
  }

  // Filled pastille (solid color + white number), centered — used on the photo bounding boxes.
  private drawPastille(x: number, y: number, num: number, color: string, r: number): void {
    this.pdf.setFillColor(color);
    this.pdf.circle(x, y, r, 'F');
    this.pdf.setTextColor('#ffffff');
    this.pdf.setFontSize(r * 1.3);
    this.pdf.text(String(num), x, y, { align: 'center', baseline: 'middle' });
  }

  // Outlined badge (white fill, colored border + number), centered — used in the report risk grid.
  private drawNumberBadge(x: number, y: number, num: number, r: number, color: string): void {
    this.pdf.setFillColor('#ffffff');
    this.pdf.setDrawColor(color);
    this.pdf.setLineWidth(0.7);
    this.pdf.circle(x, y, r, 'FD');
    this.pdf.setLineWidth(0.2);
    this.pdf.setDrawColor('#000000');
    this.pdf.setTextColor(color);
    this.pdf.setFontSize(r * 1.3);
    this.pdf.text(String(num), x, y, { align: 'center', baseline: 'middle' });
  }

  // Draw wrapped text within [startY, maxY]. If it doesn't all fit, the last visible line is
  // ellipsized with "…". Uses the caller's current font/size. Returns the y after the last line.
  private drawClampedText(text: string, x: number, startY: number, maxWidth: number, maxY: number, lineHeight: number): number {
    const lines: string[] = this.pdf.splitTextToSize(text, maxWidth);
    let ty = startY;
    for (let i = 0; i < lines.length; i++) {
      if (ty > maxY) break;
      const moreAfter = i < lines.length - 1;
      if (moreAfter && (ty + lineHeight) > maxY) {
        this.pdf.text(this.ellipsize(lines[i]!, maxWidth), x, ty);
        ty += lineHeight;
        break;
      }
      this.pdf.text(lines[i]!, x, ty);
      ty += lineHeight;
    }
    return ty;
  }

  private ellipsize(line: string, maxWidth: number): string {
    const ell = '…';
    let s = line.replace(/\s+$/, '');
    while (s.length && this.pdf.getTextWidth(s + ell) > maxWidth) {
      s = s.slice(0, -1).replace(/\s+$/, '');
    }
    return s + ell;
  }

  // Normalized bounding box for a risk: from the attachment risk tag (risks page) or, failing
  // that, from the imageStatus label matched by the shared tag id (stream/label report).
  private resolveBoxBB(entry: any, source: any, labelProject?: LabelProject): { left: number; top: number; width: number; height: number } | null {
    if (entry.box?.boundingBox) return entry.box.boundingBox;
    const tagId = entry.metric?.payload?.tagId;
    const status = (source?.statuses || []).find((s: any) => (s.labelProject?.id ?? s.labelProject) === labelProject?.id);
    const labels = (status?.labels || []).filter((l: any) => !isFaceTag(l.name));
    const label = (tagId && labels.find((l: any) => l._id === tagId)) || labels[entry.number - 1];
    const bb = label?.boundingBox;
    if (Array.isArray(bb) && bb.length === 4 && bb.every((v: any) => Number.isFinite(v))) {
      return { left: bb[0], top: bb[1], width: bb[2], height: bb[3] };
    }
    return null;
  }

  private riskIcons: Record<string, string> = {};

  private loadRiskIcons(): Promise<void> {
    const names = ['danger', 'hauteur', 'plain-pied', 'effondrement', 'levage', 'coactivite', 'ensevelissement', 'posture', 'number'];
    if (Object.keys(this.riskIcons).length) return Promise.resolve();
    return Promise.all(names.map(name => new Promise<void>(resolve => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', `/models/risks/SVG/${name}.svg`, true);
      xhr.onload = () => { this.riskIcons[name] = xhr.responseText; resolve(); };
      xhr.onerror = () => resolve();
      xhr.send(null);
    }))).then(() => { });
  }

  // Arrow-less, circle-tight red PNG of the risk pictogram for the grid cell.
  private async riskIconPng(title: string | undefined, color: string): Promise<string | null> {
    let raw = this.riskIcons[iconNameForTitle(title)];
    if (!raw) return null;
    raw = raw.replace(/<polygon\b[^>]*class="[^"]*marker-arrow[^"]*"[^>]*\/>/g, '');
    const m = raw.match(/<circle[^>]*\bcx="([\d.]+)"[^>]*\bcy="([\d.]+)"[^>]*\br="([\d.]+)"/);
    let svg = raw.replaceAll('{{ color }}', color).replace('{{ iconColor }}', '#FFF')
      .replace(/{{ size }}/g, '50').replace(/{{ scale }}/g, '1');
    if (m) {
      const cx = parseFloat(m[1]!), cy = parseFloat(m[2]!), r = parseFloat(m[3]!);
      svg = svg.replace(/viewBox="[^"]*"/, `viewBox="${cx - r} ${cy - r} ${2 * r} ${2 * r}"`);
    }
    return this.svgToPng(svg, 64);
  }

  // Red number pin (with arrow) used as the compound-image marker icon on the report map.
  private async numberIconPng(count: number, color: string): Promise<string | null> {
    const raw = this.riskIcons['number'];
    if (!raw) return null;
    const svg = raw.replaceAll('{{ color }}', color).replaceAll('{{ iconColor }}', '#FFF')
      .replaceAll('{{ number }}', String(count)).replace(/{{ size }}/g, '50').replace(/{{ scale }}/g, '1');
    return this.svgToPng(svg, 40);
  }

  private svgToPng(svg: string, size: number): Promise<string | null> {
    return new Promise(resolve => {
      const img = document.createElement('img');
      img.onload = () => {
        const cnv = document.createElement('canvas');
        cnv.width = size; cnv.height = size;
        const ctx = cnv.getContext('2d');
        if (!ctx) return resolve(null);
        ctx.drawImage(img, 0, 0, size, size);
        resolve(cnv.toDataURL());
      };
      img.onerror = () => resolve(null);
      img.src = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
    });
  }

  getImage(attachmentId: string) {
    return this.attachments.length
    ? this.attachments.find(att => att.id == attachmentId)
    : this.images.find(img => img.attachment == attachmentId);
  }
}
